'use client';
import React, { useState } from 'react';
import Spinner from '../atoms/icons/spinner';
import ArrowIcon from '../atoms/icons/arrow';
import { LinkWrapper } from '../../utils/link';

// This component is used outside of Storyblok context (hence the file name in camelCase), and is not editable in Storyblok.
const UtilityButton = ({
  label,
  onClick,
  padding = 'mb-4',
  buttonType = 'expanded',
  color = 'yellow',
  icon = <ArrowIcon />,
  parentColor = 'yellow',
  link,
  linkWrapsEntireParent = false,
  showLoader = false,
  size = 'default',
  enabled = true,
  showPackageIcon = false,
  trackingId,
}) => {
  const buttonStyle =
    'rounded-full border border-transparent transition-border duration-300 ease-in-out text-base font-crystalCondensed relative';

  //change button colors based on background color to ensure contrast
  const buttonBgColors = {
    yellow: 'bg-yellow-60',
    white: 'bg-yellow-60',

    'yellow-secondary': 'bg-yellow-60',
    'yellow-announcement': 'bg-yellow-40',
    blue: 'bg-blue-60',
    'blue-secondary': 'bg-blue-60',
    'blue-announcement': 'bg-blue-60',
    green: 'bg-green-60',
    'green-secondary': 'bg-green-60',
    'green-announcement': 'bg-green-40',
    orange: 'bg-orange-60',
    'orange-secondary': 'bg-orange-60',
    'orange-announcement': 'bg-orange-40',

    black: 'bg-black text-white',
    'button-orange-announcement': 'bg-orange text-white',
    gray: 'bg-black-30',
  };

  const buttonBgColor =
    buttonBgColors[color !== 'yellow' ? `${color}` : parentColor || 'yellow'];

  const buttonHoverColors = {
    yellow: 'group-hover:bg-yellow-40',
    white: 'group-hover:bg-yellow-40',

    'yellow-announcement': 'bg-yellow-30',
    blue: 'group-hover:bg-blue-40',
    'blue-announcement': 'bg-blue-30',
    green: 'group-hover:bg-green-40',
    'green-announcement': 'bg-green-30',
    orange: 'group-hover:bg-orange-40',
    'orange-announcement': 'bg-orange-30',
    black: 'group-hover:bg-black',
    'button-orange-announcement': '',
    gray: '',
  };

  const buttonHoverColor =
    buttonHoverColors[
      color !== 'yellow' ? `button-${color}` : parentColor || 'yellow'
    ];

  const tempIcon =
    color === 'button-orange-announcement' ||
    parentColor === 'orange-announcement' ? (
      <ArrowIcon fill="#ffffff" />
    ) : (
      icon
    );

  const [actualIcon, setActualIcon] = useState(tempIcon);
  const [loading, setLoading] = useState(false);

  const loadingIndicator = (
    <span
      className={`rounded-full ${buttonBgColor} ${
        loading ? 'animate-spin' : ''
      } ${buttonType !== 'collapsed' ? '-ml-3' : ''}  ${
        enabled ? 'group-hover:ml-0' : ''
      }  px-3.5 py-3 transition-all flex justify-center items-center`}
    >
      {actualIcon}
    </span>
  );

  const buttonSizes = {
    default: '',
    small: 'text-xs',
  };

  const buttonSize = buttonSizes[size];

  const handleClick = () => {
    if (showLoader) {
      setActualIcon(<Spinner fill="#000000" />);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setActualIcon(icon);
      }, 2000);
    }
    onClick();
  };

  // The final return statement is a conditional that determines if the button itself has a functional link or not.

  if (buttonType === 'collapsed') {
    return !linkWrapsEntireParent ? (
      <LinkWrapper
        onClick={onClick ? handleClick : null}
        className={`rounded-full hover:bg-transparent no-underline flex items-stretch align-middle group ${padding} overflow-hidden relative`}
        link={link}
        trackingId={trackingId}
      >
        {label && label.length && (
          <div
            className={`origin-left px-0 py-0 overflow-hidden w-0 h-0 max-h-0 max-w-0 group-hover:w-auto group-hover:h-auto group-hover:px-4 group-hover:py-1 group-hover:max-w-[600px] group-hover:max-h-[80px] ${buttonStyle} transition-[max-width, max-height] duration-300 ${buttonBgColor} ${buttonSize}`}
          >
            {label}
          </div>
        )}
        {loadingIndicator}
      </LinkWrapper>
    ) : (
      <div
        className={`rounded-full hover:bg-transparent no-underline flex items-stretch align-middle group ${padding} overflow-hidden relative`}
      >
        {label && label.length && (
          <div
            className={`origin-left px-0 py-0 overflow-hidden w-0 h-0 max-h-0 max-w-0 group-hover:w-auto group-hover:h-auto group-hover:px-4 group-hover:py-1 group-hover:max-w-[600px] group-hover:max-h-[80px] ${buttonStyle} transition-[max-width, max-height] duration-300 ${buttonBgColor} ${buttonSize}`}
          >
            {label}
          </div>
        )}
        {loadingIndicator}
      </div>
    );
  }
  if (buttonType === 'text') {
    return !linkWrapsEntireParent ? (
      <LinkWrapper
        className={`no-underline flex items-stretch align-middle group overflow-hidden relative ${buttonSize} `}
        link={link}
        trackingId={trackingId}
      >
        <span className={`group-hover:pr-3 py-0  ${buttonStyle}`}>{label}</span>
        <span
          className={`group-hover:ml-0 px-3.5 py-0 transition-all flex justify-center items-center`}
        >
          {actualIcon}
        </span>
      </LinkWrapper>
    ) : (
      <div
        className={`no-underline flex items-stretch align-middle group overflow-hidden relative ${buttonSize} `}
      >
        <span className={`group-hover:pr-3 py-0  ${buttonStyle}`}>{label}</span>
        <span
          className={`group-hover:ml-0 px-3.5 py-0 transition-all flex justify-center items-center`}
        >
          {actualIcon}
        </span>
      </div>
    );
  }
  if (buttonType === 'hidden') {
    return;
  } else {
    return !linkWrapsEntireParent ? (
      <LinkWrapper
        onClick={onClick ? handleClick : null}
        className={`rounded-full col-span-12 ${buttonBgColor} ${
          enabled ? 'hover:bg-transparent' : 'opacity-60'
        }  no-underline relative flex items-stretch align-middle group ${padding} transition-all w-max ${buttonHoverColor} ${buttonSize}`}
        link={link}
        trackingId={trackingId}
      >
        <span
          className={`${buttonBgColor} ${buttonHoverColor} pr-3 ${
            enabled ? 'group-hover:pr-5' : 'disabled'
          }  pl-4 py-2  ${buttonStyle}`}
        >
          {label}
        </span>
        {loadingIndicator}
      </LinkWrapper>
    ) : (
      <div
        className={`rounded-full col-span-12 ${buttonBgColor} ${
          enabled ? 'hover:bg-transparent' : 'opacity-60'
        }  no-underline relative flex items-stretch align-middle group ${padding} transition-all w-max ${buttonHoverColor} ${buttonSize}`}
      >
        <span
          className={`${buttonBgColor} ${buttonHoverColor} pr-3 ${
            enabled ? 'group-hover:pr-5' : 'disabled'
          }  pl-4 py-2  ${buttonStyle}`}
          data-tracking-id={trackingId}
        >
          {label}
        </span>
        {loadingIndicator}
      </div>
    );
  }
};

export default UtilityButton;
